import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import GroupManagement from './GroupManagement';

/**
 * Parent Dashboard - 2025 Tappy Brand Design
 *
 * Features:
 * - Multi-child overview (grid view)
 * - Individual child deep dive
 * - "How You Can Help at Home" recommendations
 * - Learning progress tracking
 * - Safety status
 * - Teacher feedback
 *
 * Design Philosophy:
 * - Tappy brand colors and micro-interactions
 * - Simplified metrics (no Bloom's taxonomy, no curiosity radar)
 * - Parent-friendly language ("Is my kid learning?")
 * - Actionable insights, not raw data
 * - iOS/Notion quality UX
 */

// Tappy Brand Colors (2025 Official Design System)
const TappyBrandColors = {
  primary: {
    teal: '#297A92',
    brightTeal: '#42909E',
    mediumTeal: '#5FA8BA',
    skyBlue: '#6DB4CD',
    aqua: '#7FC8DB',
    lightAqua: '#A8D8E6'
  },
  neutral: {
    darkSlate: '#1A3E48',
    darkGray: '#3D4954',
    brandBlue: '#4A7C8C',
    mauve: '#8E9AAE',
    mintGray: '#C5D5D8',
    lightGray: '#E8EEF0',
    paleGray: '#D5DEDD',
    nearWhite: '#F7F9FA'
  },
  accent: {
    gold: '#ECC962',
    brightGold: '#F4D97D'
  }
};

const ParentDashboard = ({ user, onLogout, userTier, hasFeatureAccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Tab state - derived from URL path
  const activeTab = location.pathname === '/family' ? 'family' : 'dashboard';
  const isHomeOrFamily = userTier === 'home' || userTier === 'expert-family';

  // Profile menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Dashboard state
  const [children, setChildren] = useState([]);
  const [familySummary, setFamilySummary] = useState(null);
  const [valueMetrics, setValueMetrics] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childDetail, setChildDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle ?child= query param to auto-select a child
  useEffect(() => {
    const childId = searchParams.get('child');
    if (childId && children.length > 0) {
      const child = children.find(c => c.id === parseInt(childId));
      if (child) {
        setSelectedChild(child);
        fetchChildDetail(child.id);
      }
    }
  }, [searchParams, children]);

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/moderation/parent/children', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch children');
      }

      const data = await response.json();
      setChildren(data.children || []);
      setFamilySummary(data.family_summary || null);
      setValueMetrics(data.value_metrics || null);
    } catch (err) {
      console.error('Error fetching children:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildDetail = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/moderation/parent/child/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch child details');
      }

      const data = await response.json();
      setChildDetail(data);
      setSelectedChild(studentId);
    } catch (err) {
      console.error('Error fetching child detail:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleBackToOverview = () => {
    setSelectedChild(null);
    setChildDetail(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <Card className="max-w-2xl mx-auto border-red-200">
          <CardHeader>
            <CardTitle className="text-red-700">Error Loading Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchChildren} variant="danger">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If a child is selected, show detailed view
  if (selectedChild && childDetail) {
    return <ChildDetailView child={childDetail} onBack={handleBackToOverview} />;
  }

  // Tab configuration
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', path: '/parent' },
    ...(isHomeOrFamily ? [{ id: 'family', label: 'Group', path: '/family' }] : [])
  ];

  // Multi-child overview
  return (
    <div className="min-h-screen" style={{ backgroundColor: TappyBrandColors.neutral.nearWhite }}>
      {/* Header - iOS-style Glassmorphism (matches SchoolAdmin/DistrictAdmin) */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 40px',
        backgroundColor: 'rgba(252, 253, 254, 0.85)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(26, 62, 72, 0.08)'
      }}>
        {/* Left: Logo + Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img
            src="/images/TAPPY-logo-black.png"
            alt="Tappy"
            style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
          />
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: 600,
              color: TappyBrandColors.neutral.darkSlate,
              letterSpacing: '-0.5px'
            }}>
              Parent Portal
            </h1>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '14px',
              color: TappyBrandColors.neutral.brandBlue
            }}>
              {isHomeOrFamily ? 'Home Plan' : 'Unlimited'} · {user?.email || user?.username}
            </p>
          </div>
        </div>

        {/* Right: Profile Menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 12px',
              backgroundColor: isMenuOpen ? TappyBrandColors.neutral.lightGray : TappyBrandColors.neutral.paleGray,
              color: TappyBrandColors.neutral.darkSlate,
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.lightGray;
            }}
            onMouseLeave={(e) => {
              if (!isMenuOpen) e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 600
            }}>
              {(user?.name || user?.username || 'P').charAt(0).toUpperCase()}
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <>
              {/* Backdrop to close menu */}
              <div
                onClick={() => setIsMenuOpen(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 99
                }}
              />
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                border: `1px solid ${TappyBrandColors.neutral.lightGray}`,
                minWidth: '240px',
                zIndex: 100,
                overflow: 'hidden'
              }}>
                {/* User Info */}
                <div style={{
                  padding: '16px',
                  borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`,
                  backgroundColor: TappyBrandColors.neutral.nearWhite
                }}>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: TappyBrandColors.neutral.darkSlate,
                    marginBottom: '2px'
                  }}>
                    {user?.name || user?.username || 'Parent'}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: TappyBrandColors.neutral.mauve,
                    marginBottom: '8px'
                  }}>
                    {user?.email || ''}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    backgroundColor: isHomeOrFamily ? TappyBrandColors.accent.gold : TappyBrandColors.primary.skyBlue,
                    color: isHomeOrFamily ? '#78350F' : 'white',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}>
                    {isHomeOrFamily ? 'Home Plan' : 'Unlimited'}
                  </div>
                </div>

                {/* Menu Items */}
                <div style={{ padding: '8px 0' }}>
                  <button
                    onClick={() => { setIsMenuOpen(false); navigate('/subscription'); }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: TappyBrandColors.neutral.darkSlate,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    Account Settings
                  </button>

                  {/* Back to Tutor - only for non-home tier */}
                  {userTier !== 'home' && (
                    <button
                      onClick={() => { setIsMenuOpen(false); navigate('/tutor'); }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: TappyBrandColors.neutral.darkSlate,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                        <line x1="12" x2="12" y1="19" y2="22"/>
                      </svg>
                      Start Learning
                    </button>
                  )}
                </div>

                {/* Logout */}
                <div style={{
                  padding: '8px',
                  borderTop: `1px solid ${TappyBrandColors.neutral.lightGray}`
                }}>
                  <button
                    onClick={() => { setIsMenuOpen(false); onLogout(); }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#DC2626',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      borderRadius: '8px',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.08)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" x2="9" y1="12" y2="12"/>
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation - Clean iOS-style */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '0 40px',
        backgroundColor: '#ffffff',
        borderBottom: `1px solid ${TappyBrandColors.neutral.lightGray}`
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            style={{
              padding: '14px 24px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? `3px solid ${TappyBrandColors.primary.teal}` : '3px solid transparent',
              color: activeTab === tab.id ? TappyBrandColors.primary.teal : TappyBrandColors.neutral.brandBlue,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? 600 : 500,
              transition: 'all 200ms ease',
              letterSpacing: '-0.2px'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = TappyBrandColors.neutral.darkSlate;
                e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = TappyBrandColors.neutral.brandBlue;
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'family' ? (
        <div style={{ padding: '40px', backgroundColor: TappyBrandColors.neutral.nearWhite, minHeight: 'calc(100vh - 140px)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <GroupManagement
              user={user}
              hasFeatureAccess={(feature) => {
                if (feature === 'GROUP_MANAGEMENT') return true;
                return hasFeatureAccess(feature);
              }}
            />
          </div>
        </div>
      ) : (
      /* Dashboard Content */
      <div style={{ padding: '40px', backgroundColor: TappyBrandColors.neutral.nearWhite, minHeight: 'calc(100vh - 140px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ padding: '32px' }}>
        <div className="max-w-7xl mx-auto">
          {/* Page Title */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 600,
              color: TappyBrandColors.neutral.darkSlate,
              margin: 0,
              marginBottom: '8px',
              letterSpacing: '-0.5px'
            }}>
              My Children
            </h2>
            <p style={{
              fontSize: '15px',
              color: TappyBrandColors.neutral.mauve,
              margin: 0
            }}>
              Track learning progress and celebrate achievements
            </p>
          </div>

        {/* Empty state - Professional Style (Notion/Slack inspired) */}
        {children.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '64px 48px',
            textAlign: 'center',
            border: `2px solid ${TappyBrandColors.neutral.lightGray}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              backgroundColor: TappyBrandColors.neutral.paleGray,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              border: `2px solid ${TappyBrandColors.neutral.lightGray}`
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={TappyBrandColors.primary.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: TappyBrandColors.neutral.darkSlate,
              marginBottom: '8px'
            }}>
              No children found
            </h3>
            <p style={{
              fontSize: '14px',
              color: TappyBrandColors.neutral.mauve,
              lineHeight: '1.6',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              Learning data will appear here once your children start using Tappy
            </p>
          </div>
        ) : (
          <>
            {/* Child Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {children.map((child) => (
                <ChildCard
                  key={child.student_id}
                  child={child}
                  onClick={() => fetchChildDetail(child.student_id)}
                />
              ))}
            </div>

            {/* Family Summary - Professional Style */}
            {familySummary && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '28px',
                border: `2px solid ${TappyBrandColors.primary.teal}`,
                borderLeft: `4px solid ${TappyBrandColors.primary.teal}`,
                boxShadow: '0 2px 8px rgba(41, 122, 146, 0.12)'
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: TappyBrandColors.neutral.darkSlate,
                  marginBottom: '20px'
                }}>
                  Group Learning Overview — This Week
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '24px'
                }}>
                  <div>
                    <p style={{ fontSize: '13px', color: TappyBrandColors.neutral.mauve, marginBottom: '8px', fontWeight: 500 }}>
                      Total Time
                    </p>
                    <p style={{ fontSize: '32px', fontWeight: 700, color: TappyBrandColors.primary.teal, marginBottom: '4px' }}>
                      {Math.floor(familySummary.total_time_this_week_minutes / 60)}h {familySummary.total_time_this_week_minutes % 60}m
                    </p>
                    <p style={{ fontSize: '13px', color: '#10B981', fontWeight: 600 }}>
                      {familySummary.time_change_percent >= 0 ? '↑' : '↓'} {Math.abs(familySummary.time_change_percent)}% from last week
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '13px', color: TappyBrandColors.neutral.mauve, marginBottom: '8px', fontWeight: 500 }}>
                      Children Needing Support
                    </p>
                    <p style={{ fontSize: '32px', fontWeight: 700, color: TappyBrandColors.neutral.darkSlate }}>
                      {familySummary.children_needing_support}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '13px', color: TappyBrandColors.neutral.mauve, marginBottom: '8px', fontWeight: 500 }}>
                      Overall Status
                    </p>
                    <div style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      backgroundColor: familySummary.children_needing_support > 0 ? '#FEF3C7' : '#D1FAE5',
                      border: `1.5px solid ${familySummary.children_needing_support > 0 ? '#F59E0B' : '#10B981'}`,
                      fontSize: '14px',
                      fontWeight: 600,
                      color: familySummary.children_needing_support > 0 ? '#92400E' : '#065F46'
                    }}>
                      {familySummary.children_needing_support > 0 ? 'Some need help' : 'All on track'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Your Investment Value - ROI Display for B2C Parents Only */}
            {valueMetrics && valueMetrics.is_b2c && valueMetrics.total_hours_this_month > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '28px',
                marginTop: '24px',
                border: `2px solid ${TappyBrandColors.accent.gold}`,
                borderLeft: `4px solid ${TappyBrandColors.accent.gold}`,
                boxShadow: '0 2px 8px rgba(236, 201, 98, 0.15)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
                  <div>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: TappyBrandColors.neutral.darkSlate,
                      marginBottom: '8px'
                    }}>
                      Your Investment Value — This Month
                    </h3>
                    <p style={{ fontSize: '14px', color: TappyBrandColors.neutral.mauve, marginBottom: '16px' }}>
                      Equivalent to {valueMetrics.total_hours_this_month} hours of private tutoring
                    </p>
                    <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                      <div>
                        <p style={{ fontSize: '13px', color: TappyBrandColors.neutral.mauve, marginBottom: '4px', fontWeight: 500 }}>
                          Tutoring Value
                        </p>
                        <p style={{ fontSize: '28px', fontWeight: 700, color: '#10B981' }}>
                          ${valueMetrics.equivalent_tutoring_value.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve }}>
                          at $50/hr market rate
                        </p>
                      </div>
                      <div>
                        <p style={{ fontSize: '13px', color: TappyBrandColors.neutral.mauve, marginBottom: '4px', fontWeight: 500 }}>
                          Your Cost
                        </p>
                        <p style={{ fontSize: '28px', fontWeight: 700, color: TappyBrandColors.primary.teal }}>
                          ${valueMetrics.monthly_cost}
                        </p>
                        <p style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve }}>
                          per month
                        </p>
                      </div>
                      {valueMetrics.roi_multiple > 1 && (
                        <div>
                          <p style={{ fontSize: '13px', color: TappyBrandColors.neutral.mauve, marginBottom: '4px', fontWeight: 500 }}>
                            Return on Investment
                          </p>
                          <p style={{ fontSize: '28px', fontWeight: 700, color: TappyBrandColors.accent.gold }}>
                            {valueMetrics.roi_multiple}x
                          </p>
                          <p style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve }}>
                            value multiplier
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Celebration Moments - Shareable Achievements */}
            {children.length > 0 && children.some(c => c.overall_mastery >= 0.6 || c.sessions_this_week >= 3) && (
              <CelebrationCard children={children} valueMetrics={valueMetrics} />
            )}

            {/* Recommend to School - Advocacy CTA (B2C Only) */}
            {valueMetrics?.is_b2c && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '28px',
                marginTop: '24px',
                border: `2px solid ${TappyBrandColors.primary.skyBlue}`,
                boxShadow: '0 2px 8px rgba(109, 180, 205, 0.12)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    backgroundColor: TappyBrandColors.neutral.nearWhite,
                    border: `2px solid ${TappyBrandColors.primary.skyBlue}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={TappyBrandColors.primary.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                      <path d="M12 6v7"></path>
                      <path d="M15 9l-3-3-3 3"></path>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: TappyBrandColors.neutral.darkSlate,
                      marginBottom: '6px'
                    }}>
                      Love Tappy? Share it with your school!
                    </h3>
                    <p style={{ fontSize: '14px', color: TappyBrandColors.neutral.mauve, lineHeight: '1.5' }}>
                      When schools adopt Tappy, every student gets access — and families like yours get it free.
                      Help bring Tappy to your child's classroom.
                    </p>
                  </div>
                  <button
                    onClick={() => window.open('mailto:schools@teachtappy.com?subject=Parent%20Recommendation%20-%20Bring%20Tappy%20to%20Our%20School&body=Hi%20Tappy%20Team%2C%0A%0AI%27m%20a%20parent%20who%20loves%20using%20Tappy%20with%20my%20child%20and%20would%20like%20to%20recommend%20it%20to%20our%20school.%0A%0ASchool%20Name%3A%20%0ASchool%20District%3A%20%0APrincipal%20%2F%20Contact%3A%20%0A%0AThank%20you!', '_blank')}
                    style={{
                      padding: '14px 28px',
                      backgroundColor: TappyBrandColors.primary.teal,
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: 600,
                      transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`,
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Recommend to School
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>
      </div>
      </div>
      )}

      {/* Tappy Footer - Consistent with SchoolAdmin/DistrictAdmin */}
      <div style={{
        marginTop: '60px',
        backgroundColor: 'rgba(245, 249, 250, 0.95)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(213, 222, 221, 0.5)',
        padding: '1rem',
        textAlign: 'center',
        fontSize: '0.65rem',
        color: '#76909f'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.3rem',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <img
            src="/images/TAPPY-logo-black.png"
            alt="Tappy Logo"
            style={{
              height: '18px',
              width: 'auto',
              objectFit: 'contain',
              marginBottom: '0.2rem'
            }}
          />
          <p style={{ margin: 0, fontSize: '0.65rem', lineHeight: '1.4' }}>
            &copy; {new Date().getFullYear()} Teach Tappy | Powered by <span style={{ fontWeight: 500 }}>Morpheus RL</span>
          </p>
          <p style={{ margin: 0, fontSize: '0.6rem', lineHeight: '1.4' }}>
            By using our services, you agree to Tappy's{' '}
            <a
              href="/terms-of-service.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#76909f',
                textDecoration: 'underline'
              }}
            >
              Terms of Use
            </a>
            {' '}and{' '}
            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#76909f',
                textDecoration: 'underline'
              }}
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Child Card Component - Tappy Brand Style with 2025 Micro-interactions
 */
const ChildCard = ({ child, onClick }) => {
  const needsHelp = child.status === 'needs_support';
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '16px',
        border: `2px solid ${isHovered ? TappyBrandColors.primary.teal : TappyBrandColors.neutral.mintGray}`,
        cursor: 'pointer',
        transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        boxShadow: isHovered ? `0 8px 24px rgba(41, 122, 146, 0.15)` : '0 1px 3px rgba(26, 62, 72, 0.08)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: TappyBrandColors.primary.darkTeal,
            marginBottom: '4px'
          }}>
            {child.name}
          </h3>
          <p style={{
            fontSize: '13px',
            color: TappyBrandColors.primary.brandBlue
          }}>
            {child.grade && `Grade ${child.grade} • `}{child.school || 'Learning with Tappy'}
          </p>
        </div>
        {needsHelp && (
          <div style={{
            padding: '6px 12px',
            borderRadius: '6px',
            backgroundColor: '#FEF3C7',
            fontSize: '12px',
            fontWeight: 600,
            color: '#92400E',
            whiteSpace: 'nowrap',
            letterSpacing: '0.3px'
          }}>
            NEEDS SUPPORT
          </div>
        )}
        {!needsHelp && (
          <div style={{
            padding: '6px 12px',
            borderRadius: '6px',
            backgroundColor: '#D1FAE5',
            fontSize: '12px',
            fontWeight: 600,
            color: '#065F46',
            whiteSpace: 'nowrap',
            letterSpacing: '0.3px'
          }}>
            ON TRACK
          </div>
        )}
      </div>

      {/* Overall Progress - Tappy Brand */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
          <span style={{ color: TappyBrandColors.primary.brandBlue, fontWeight: 500 }}>Overall Progress</span>
          <span style={{ fontWeight: 600, color: TappyBrandColors.neutral.darkSlate }}>
            {(child.overall_mastery * 100).toFixed(0)}%
          </span>
        </div>
        <div style={{
          width: '100%',
          backgroundColor: TappyBrandColors.neutral.lightGray,
          borderRadius: '20px',
          height: '10px',
          overflow: 'hidden'
        }}>
          <div
            style={{
              height: '10px',
              borderRadius: '20px',
              transition: 'all 300ms ease-out',
              width: `${child.overall_mastery * 100}%`,
              backgroundColor: child.overall_mastery >= 0.8 ? '#10B981' :
                              child.overall_mastery >= 0.6 ? TappyBrandColors.primary.skyBlue :
                              TappyBrandColors.accent.gold
            }}
          />
        </div>
      </div>

      {/* This Week Stats - Tappy Brand */}
      <div style={{ marginBottom: '16px' }}>
        <p style={{ fontSize: '13px', color: TappyBrandColors.primary.brandBlue, fontWeight: 600, marginBottom: '8px' }}>
          This Week:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          <div>
            <p style={{ fontSize: '11px', color: TappyBrandColors.neutral.mauve, marginBottom: '4px' }}>Sessions</p>
            <p style={{ fontSize: '18px', fontWeight: 600, color: TappyBrandColors.neutral.darkSlate }}>
              {child.sessions_this_week}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '11px', color: TappyBrandColors.neutral.mauve, marginBottom: '4px' }}>Minutes</p>
            <p style={{ fontSize: '18px', fontWeight: 600, color: TappyBrandColors.neutral.darkSlate }}>
              {child.minutes_this_week}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '11px', color: TappyBrandColors.neutral.mauve, marginBottom: '4px' }}>Questions</p>
            <p style={{ fontSize: '18px', fontWeight: 600, color: TappyBrandColors.neutral.darkSlate }}>
              {child.questions_this_week}
            </p>
          </div>
        </div>
      </div>

      {/* Needs Help Section - Tappy Brand */}
      {needsHelp && child.needs_help_with && (
        <div style={{
          padding: '12px',
          backgroundColor: '#FEF3C7',
          border: `1.5px solid ${TappyBrandColors.accent.gold}`,
          borderRadius: '10px',
          marginBottom: '16px'
        }}>
          <p style={{ fontSize: '13px', fontWeight: 600, color: '#92400E', marginBottom: '4px' }}>
            Struggling with:
          </p>
          <p style={{ fontSize: '13px', color: '#78350F' }}>
            {child.needs_help_with.topic} ({(child.needs_help_with.mastery * 100).toFixed(0)}% mastery)
          </p>
        </div>
      )}

      {/* View Details Button - Tappy Brand */}
      <button
        style={{
          width: '100%',
          padding: '10px 16px',
          backgroundColor: TappyBrandColors.primary.teal,
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 150ms ease',
          boxShadow: '0 2px 4px rgba(41, 122, 146, 0.2)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(41, 122, 146, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(41, 122, 146, 0.2)';
        }}
      >
        View Details →
      </button>
    </div>
  );
};

/**
 * Celebration Card Component - Shareable achievements
 * iOS/Notion quality design matching SchoolAdmin dashboard
 */
const CelebrationCard = ({ children, valueMetrics }) => {
  const [copied, setCopied] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  // Calculate achievements
  const achievements = [];

  children.forEach(child => {
    if (child.overall_mastery >= 0.8) {
      achievements.push({
        type: 'mastery',
        icon: '🌟',
        title: 'Excellence Achievement',
        description: `${child.name} reached ${(child.overall_mastery * 100).toFixed(0)}% mastery`,
        child: child.name
      });
    } else if (child.overall_mastery >= 0.6) {
      achievements.push({
        type: 'progress',
        icon: '📈',
        title: 'Strong Progress',
        description: `${child.name} is making great strides`,
        child: child.name
      });
    }

    if (child.sessions_this_week >= 5) {
      achievements.push({
        type: 'dedication',
        icon: '🔥',
        title: 'Learning Streak',
        description: `${child.name} completed ${child.sessions_this_week} sessions this week`,
        child: child.name
      });
    } else if (child.sessions_this_week >= 3) {
      achievements.push({
        type: 'consistency',
        icon: '✨',
        title: 'Consistent Learner',
        description: `${child.name} stayed on track with ${child.sessions_this_week} sessions`,
        child: child.name
      });
    }

    if (child.questions_this_week >= 20) {
      achievements.push({
        type: 'curiosity',
        icon: '🧠',
        title: 'Curious Mind',
        description: `${child.name} asked ${child.questions_this_week} questions this week`,
        child: child.name
      });
    }
  });

  // Generate shareable text
  const generateShareText = () => {
    const topAchievement = achievements[0];
    if (!topAchievement) return '';

    let text = `${topAchievement.icon} ${topAchievement.title}!\n\n`;
    text += `${topAchievement.description} with Tappy AI Tutor.\n\n`;

    // Only include ROI metrics for B2C parents
    if (valueMetrics && valueMetrics.is_b2c && valueMetrics.roi_multiple > 1) {
      text += `This month: ${valueMetrics.total_hours_this_month} hours of learning = $${valueMetrics.equivalent_tutoring_value} tutoring value\n\n`;
    }

    text += `Learn more at teachtappy.com`;
    return text;
  };

  const handleShare = async () => {
    const text = generateShareText();

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Tappy Learning Achievement',
          text: text,
        });
      } catch (err) {
        // User cancelled or error - fall back to clipboard
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } else {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (achievements.length === 0) return null;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '28px',
        marginTop: '24px',
        border: `2px solid ${TappyBrandColors.neutral.lightGray}`,
        boxShadow: isHovered
          ? '0 8px 24px rgba(26, 62, 72, 0.12)'
          : '0 1px 3px rgba(26, 62, 72, 0.08)',
        transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${TappyBrandColors.accent.gold} 0%, ${TappyBrandColors.accent.brightGold} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(236, 201, 98, 0.3)'
          }}>
            <span style={{ fontSize: '20px' }}>🎉</span>
          </div>
          <div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: TappyBrandColors.neutral.darkSlate,
              margin: 0,
              letterSpacing: '-0.3px'
            }}>
              Celebrate This Week
            </h3>
            <p style={{
              fontSize: '13px',
              color: TappyBrandColors.neutral.mauve,
              margin: 0
            }}>
              {achievements.length} achievement{achievements.length !== 1 ? 's' : ''} earned
            </p>
          </div>
        </div>

        <button
          onClick={handleShare}
          style={{
            padding: '10px 20px',
            backgroundColor: copied ? '#D1FAE5' : TappyBrandColors.neutral.paleGray,
            color: copied ? '#065F46' : TappyBrandColors.neutral.darkSlate,
            border: `1.5px solid ${copied ? '#10B981' : TappyBrandColors.neutral.lightGray}`,
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            transition: 'all 200ms ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            if (!copied) {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.lightGray;
              e.currentTarget.style.borderColor = TappyBrandColors.primary.teal;
            }
          }}
          onMouseLeave={(e) => {
            if (!copied) {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              e.currentTarget.style.borderColor = TappyBrandColors.neutral.lightGray;
            }
          }}
        >
          {copied ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,6 12,2 8,6" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="2" x2="12" y2="15" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Share
            </>
          )}
        </button>
      </div>

      {/* Achievement Pills */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {achievements.slice(0, 4).map((achievement, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              backgroundColor: TappyBrandColors.neutral.nearWhite,
              border: `1.5px solid ${TappyBrandColors.neutral.lightGray}`,
              borderRadius: '12px',
              flex: '1 1 calc(50% - 6px)',
              minWidth: '200px'
            }}
          >
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: 'white',
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              flexShrink: 0
            }}>
              {achievement.icon}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{
                fontSize: '14px',
                fontWeight: 600,
                color: TappyBrandColors.neutral.darkSlate,
                margin: 0,
                marginBottom: '2px'
              }}>
                {achievement.title}
              </p>
              <p style={{
                fontSize: '12px',
                color: TappyBrandColors.neutral.mauve,
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {achievement.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ROI context if available (B2C only) */}
      {valueMetrics && valueMetrics.is_b2c && valueMetrics.roi_multiple > 1 && (
        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: TappyBrandColors.neutral.nearWhite,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <p style={{
            fontSize: '14px',
            color: TappyBrandColors.neutral.darkSlate,
            margin: 0
          }}>
            <span style={{ fontWeight: 600 }}>{valueMetrics.total_hours_this_month} hours</span>
            {' '}of learning this month
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              fontSize: '13px',
              color: TappyBrandColors.neutral.mauve
            }}>
              Worth
            </span>
            <span style={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#10B981'
            }}>
              ${valueMetrics.equivalent_tutoring_value.toLocaleString()}
            </span>
            <span style={{
              fontSize: '13px',
              color: TappyBrandColors.neutral.mauve
            }}>
              in tutoring
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Child Detail View Component - Individual child deep dive
 */
const ChildDetailView = ({ child, onBack }) => {
  const { student_info, progress, current_topics, mastered_topics, how_to_help, recent_sessions, teacher_feedback, safety_status, safety_wellbeing } = child;

  // Learning style state
  const [learningStyle, setLearningStyle] = useState(null);
  const [learningStyleLoading, setLearningStyleLoading] = useState(true);

  // Fetch learning style data
  useEffect(() => {
    const fetchLearningStyle = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/analytics/learning-style/${student_info.user_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.has_data) {
            setLearningStyle(data.learning_style);
          }
        }
      } catch (error) {
        console.error('Error fetching learning style:', error);
      } finally {
        setLearningStyleLoading(false);
      }
    };

    if (student_info?.user_id) {
      fetchLearningStyle();
    }
  }, [student_info]);

  // Calculate quick status indicators
  const hasTopicsNeedingHelp = current_topics && current_topics.some(t => t.needs_help);
  const safetyAllClear = !safety_wellbeing?.safety_flag && (safety_wellbeing?.content_safety?.flags_count === 0);
  const healthyScreenTime = safety_wellbeing?.healthy_usage?.healthy_patterns?.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <Button variant="ghost" onClick={onBack} className="mb-6">
          ← Back to All Children
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{student_info.name}</h1>
          <p className="text-gray-600">
            {student_info.grade && `Grade ${student_info.grade} • `}
            {student_info.school}
            {student_info.teacher_name && ` • ${student_info.teacher_name}'s Class`}
          </p>
        </div>

        {/* Quick Status Card - Parent Priority #1 */}
        <Card className="mb-8" style={{
          border: `2px solid ${TappyBrandColors.primary.teal}`,
          borderLeft: `4px solid ${TappyBrandColors.primary.teal}`,
          boxShadow: '0 2px 8px rgba(41, 122, 146, 0.12)'
        }}>
          <CardHeader style={{ backgroundColor: TappyBrandColors.neutral.nearWhite }}>
            <CardTitle style={{ color: TappyBrandColors.primary.teal }}>Quick Status</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                {/* Learning Status */}
                <div className="flex items-center gap-3">
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: hasTopicsNeedingHelp ? '#FEF3C7' : '#D1FAE5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {hasTopicsNeedingHelp ? (
                      <span style={{ fontSize: '18px' }}>💡</span>
                    ) : (
                      <span style={{ fontSize: '18px' }}>✅</span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {hasTopicsNeedingHelp ? 'Needs help in 1-2 topics' : 'Learning on track'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {progress.overall_mastery >= 0.7 ? 'Strong understanding' :
                       progress.overall_mastery >= 0.5 ? 'Good progress' : 'Building skills'}
                    </p>
                  </div>
                </div>

                {/* Safety Status */}
                <div className="flex items-center gap-3">
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: safetyAllClear ? '#D1FAE5' : '#FEF3C7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: '18px' }}>{safetyAllClear ? '🛡️' : '✓'}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {safetyAllClear ? 'All interactions safe' : 'Safety reviewed & clear'}
                    </p>
                    <p className="text-xs text-gray-600">Content automatically monitored</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Screen Time */}
                <div className="flex items-center gap-3">
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: healthyScreenTime ? '#D1FAE5' : TappyBrandColors.neutral.lightGray,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: '18px' }}>⏱️</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {healthyScreenTime ? 'Healthy screen time' : 'Tracking usage'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {safety_wellbeing?.healthy_usage?.avg_minutes || 0} min avg session
                    </p>
                  </div>
                </div>

                {/* Independence */}
                {safety_wellbeing?.independence && (
                  <div className="flex items-center gap-3">
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: safety_wellbeing.independence.score >= 70 ? '#D1FAE5' :
                                     safety_wellbeing.independence.score >= 50 ? '#DBEAFE' : '#FEF3C7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '18px' }}>
                        {safety_wellbeing.independence.score >= 70 ? '🎯' :
                         safety_wellbeing.independence.score >= 50 ? '📚' : '🤔'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {safety_wellbeing.independence.status === 'independent' ? 'Thinking independently' :
                         safety_wellbeing.independence.status === 'balanced' ? 'Good independence' :
                         'Building independence'}
                      </p>
                      <p className="text-xs text-gray-600">
                        {safety_wellbeing.independence.score}% independent learning
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Learning Style Card - Phase 3.5 */}
        {!learningStyleLoading && learningStyle && (
          <Card className="mb-8" style={{
            border: `2px solid ${TappyBrandColors.primary.skyBlue}`,
            borderLeft: `4px solid ${TappyBrandColors.primary.skyBlue}`,
            boxShadow: '0 2px 8px rgba(109, 180, 205, 0.12)'
          }}>
            <CardHeader style={{ backgroundColor: TappyBrandColors.neutral.nearWhite }}>
              <CardTitle style={{ color: TappyBrandColors.primary.teal }}>
                🎨 How {student_info.name.split(' ')[0]} Learns Best
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div style={{
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: learningStyle.dominant.confidence === 'high' ? '#D1FAE5' :
                                   learningStyle.dominant.confidence === 'medium' ? '#DBEAFE' : '#FEF3C7',
                    fontSize: '24px'
                  }}>
                    {learningStyle.dominant.style === 'visual' ? '👁️' :
                     learningStyle.dominant.style === 'auditory' ? '👂' :
                     learningStyle.dominant.style === 'reading' ? '📚' : '✋'}
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">{learningStyle.dominant.name}</p>
                    <p className="text-sm text-gray-600">
                      {(learningStyle.dominant.weight * 100).toFixed(0)}% preference •{' '}
                      {learningStyle.dominant.confidence === 'high' ? 'Strong pattern' :
                       learningStyle.dominant.confidence === 'medium' ? 'Clear preference' : 'Developing'}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-gray-700 mb-4">{learningStyle.dominant.description}</p>

                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">💪 Strengths:</p>
                  <ul className="space-y-1">
                    {learningStyle.dominant.strengths.map((strength, idx) => (
                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-green-500">✓</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for Home:</p>
                  <ul className="space-y-2">
                    {learningStyle.dominant.tips.map((tip, idx) => (
                      <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                        <span className="flex-shrink-0">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Learning Style Distribution */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-600 mb-3">Learning style mix:</p>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(learningStyle.weights).map(([style, weight]) => (
                    <div key={style} className="text-center">
                      <div className="text-xs font-medium text-gray-900 mb-1 capitalize">{style}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${weight * 100}%`,
                            backgroundColor: weight > 0.35 ? TappyBrandColors.primary.teal :
                                           weight > 0.25 ? TappyBrandColors.primary.skyBlue :
                                           TappyBrandColors.neutral.mintGray
                          }}
                        />
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{(weight * 100).toFixed(0)}%</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Based on {learningStyle.sessions_analyzed} learning session{learningStyle.sessions_analyzed !== 1 ? 's' : ''}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* How You Can Help at Home - PRIORITY #1 FOR PARENTS */}
        {how_to_help && (
          <Card className="mb-8" style={{
            border: `2px solid ${TappyBrandColors.accent.gold}`,
            borderLeft: `4px solid ${TappyBrandColors.accent.gold}`
          }}>
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-blue-900">💡 How You Can Help at Home</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Priority: Help with</p>
                <p className="text-lg font-semibold text-gray-900 mb-3">{how_to_help.priority_topic}</p>
                <p className="text-sm text-gray-700 mb-4">{how_to_help.struggle_description}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-900 mb-2">Try these activities:</p>
                <ul className="space-y-2">
                  {how_to_help.suggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-blue-500 flex-shrink-0">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" strokeWidth="2"></circle>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6l4 2"></path>
                </svg>
                <div className="text-sm">
                  <p className="text-blue-900 font-medium">Best time to practice:</p>
                  <p className="text-blue-800">{how_to_help.best_practice_time}</p>
                </div>
              </div>

              {how_to_help.teacher_plan && (
                <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  <div className="text-sm">
                    <p className="text-green-900 font-medium">Teacher's plan:</p>
                    <p className="text-green-800">{how_to_help.teacher_plan}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Learning Progress */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Learning Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Overall Understanding</span>
                  <span className="text-2xl font-bold text-gray-900">{(progress.overall_mastery * 100).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div
                    className="h-3 rounded-full transition-all"
                    style={{
                      width: `${progress.overall_mastery * 100}%`,
                      backgroundColor: progress.overall_mastery >= 0.7 ? '#10B981' :
                                     progress.overall_mastery >= 0.5 ? TappyBrandColors.primary.skyBlue :
                                     TappyBrandColors.accent.gold
                    }}
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {progress.overall_mastery >= 0.8 ? '🌟 Excellent! Strong grasp of topics' :
                   progress.overall_mastery >= 0.6 ? '✓ Good progress across subjects' :
                   progress.overall_mastery >= 0.4 ? '📚 Building understanding steadily' :
                   '💪 Just getting started - keep practicing!'}
                </p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Sessions This Week</span>
                  <span className="font-medium">{progress.sessions_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Minutes</span>
                  <span className="font-medium">{progress.total_minutes || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Current Focus</CardTitle>
            </CardHeader>
            <CardContent>
              {current_topics && current_topics.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600 mb-3">Working on:</p>
                  <div className="space-y-2 mb-4">
                    {current_topics.map((topic, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm text-gray-900">{topic.topic}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">
                            {topic.mastery >= 0.8 ? 'Strong' :
                             topic.mastery >= 0.6 ? 'Good' :
                             topic.mastery >= 0.4 ? 'Building' : 'Starting'}
                          </span>
                          {topic.needs_help && <Badge variant="warning" className="text-xs">Needs help</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {mastered_topics && mastered_topics.length > 0 && (
                    <>
                      <p className="text-sm text-gray-600 mb-2 mt-4">Got it down! ✓</p>
                      <div className="space-y-1">
                        {mastered_topics.map((topic, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                            <span className="text-gray-700">{topic.topic}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-600">No active topics yet. Keep practicing!</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Learning Independence - Standalone Section */}
        {safety_wellbeing?.independence && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Learning Independence</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                {/* Score Display */}
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-24 h-24 rounded-full" style={{
                    backgroundColor: safety_wellbeing.independence.score >= 70 ? '#D1FAE5' :
                                   safety_wellbeing.independence.score >= 40 ? '#DBEAFE' : '#FEF3C7'
                  }}>
                    <span className="text-3xl font-bold" style={{
                      color: safety_wellbeing.independence.score >= 70 ? '#059669' :
                             safety_wellbeing.independence.score >= 40 ? TappyBrandColors.primary.teal :
                             TappyBrandColors.accent.gold
                    }}>
                      {safety_wellbeing.independence.score}
                    </span>
                  </div>
                </div>

                {/* Interpretation & Insights */}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    {safety_wellbeing.independence.interpretation}
                  </p>

                  {safety_wellbeing.independence.metrics && (
                    <>
                      <div className="flex gap-4 text-xs text-gray-600 mb-3">
                        <span>{safety_wellbeing.independence.metrics.topic_diversity} topics explored</span>
                        <span>•</span>
                        <span>{safety_wellbeing.independence.metrics.questions_asked} questions asked</span>
                      </div>

                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-900">
                          <strong>How you can help:</strong> {
                            safety_wellbeing.independence.score >= 70
                              ? `Great work! Keep encouraging their curiosity and self-directed exploration.`
                              : safety_wellbeing.independence.score >= 50
                              ? `Encourage them to ask more "why" and "how" questions before looking up answers.`
                              : `Try asking them to explain what they learned in their own words before seeking AI help.`
                          }
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Sessions */}
        {recent_sessions && recent_sessions.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Recent Learning Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recent_sessions.map((session, idx) => (
                  <div key={idx} className="pb-4 border-b border-gray-200 last:border-b-0 last:pb-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">
                          {new Date(session.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                        </p>
                        <p className="text-sm text-gray-600">{session.duration_minutes} minutes</p>
                      </div>
                      {session.mastery_before !== null && session.mastery_after !== null && (
                        <Badge variant="success">
                          +{((session.mastery_after - session.mastery_before) * 100).toFixed(0)}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mb-1">
                      <strong>Practiced:</strong> {session.topic}
                    </p>
                    <p className="text-sm text-gray-600">{session.summary}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Teacher Feedback & Safety */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Teacher Feedback */}
          {teacher_feedback && teacher_feedback.latest_note && (
            <Card>
              <CardHeader>
                <CardTitle>Teacher Feedback</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-3">
                  <p className="text-sm text-gray-600">
                    From {teacher_feedback.teacher_name} • {new Date(teacher_feedback.date).toLocaleDateString()}
                  </p>
                </div>
                <p className="text-sm text-gray-700 italic">"{teacher_feedback.latest_note}"</p>
              </CardContent>
            </Card>
          )}

        </div>

        {/* Safety & Wellbeing */}
        {safety_wellbeing && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Safety &amp; Screen Time</h2>

            {/* Safety & Screen Time Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Safety Column */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"></path>
                      </svg>
                      <span className="font-medium text-gray-900">
                        {safety_wellbeing.content_safety.status === 'all_clear' ? 'All interactions safe' : 'Reviewed & resolved'}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sessions monitored:</span>
                        <span className="font-medium">{safety_wellbeing.content_safety.sessions_monitored}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Safety flags:</span>
                        <span className="font-medium">{safety_wellbeing.content_safety.flags_count}</span>
                      </div>
                    </div>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-900">
                        <strong>What we monitor:</strong> All conversations are automatically scanned for inappropriate content, including toxicity and profanity.
                      </p>
                    </div>
                  </div>

                  {/* Screen Time Column */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">This Week's Activity</h3>

                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total sessions:</span>
                        <span className="font-medium">{safety_wellbeing.healthy_usage.session_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total time:</span>
                        <span className="font-medium">
                          {Math.floor(safety_wellbeing.healthy_usage.total_minutes / 60)}h {safety_wellbeing.healthy_usage.total_minutes % 60}m
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Average session:</span>
                        <span className="font-medium">{safety_wellbeing.healthy_usage.avg_minutes} min</span>
                      </div>
                    </div>

                    {safety_wellbeing.healthy_usage.healthy_patterns && safety_wellbeing.healthy_usage.healthy_patterns.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-700">Healthy patterns:</p>
                        {safety_wellbeing.healthy_usage.healthy_patterns.map((pattern, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-gray-700">
                            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                            <span>{pattern}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Safety Flag Disclosure (if exists) */}
            {safety_wellbeing.safety_flag && (
              <Card className="mt-6 border-orange-200">
                <CardHeader className="bg-orange-50">
                  <CardTitle className="text-orange-900">Safety Review Completed</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          Date: {new Date(safety_wellbeing.safety_flag.date).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-700 mb-2">
                          Status: <span className="font-medium text-green-700">{safety_wellbeing.safety_flag.status}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Reviewed by: {safety_wellbeing.safety_flag.reviewed_by}
                        </p>
                        {!safety_wellbeing.safety_flag.action_needed && (
                          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                            <p className="text-xs text-green-900">
                              <strong>No action needed.</strong> This issue has been resolved. Our team continues to monitor for safety.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Privacy Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              <span className="text-xs text-gray-600">COPPA & FERPA Compliant</span>
            </div>
            <span className="text-gray-400">•</span>
            <span className="text-xs text-gray-600">Student privacy protected</span>
            <span className="text-gray-400">•</span>
            <span className="text-xs text-gray-600">No third-party data sharing</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;
